<template>
  <div class="selector-wrap">
    <div
      v-for="(rowTags, rowIndex) in rowTagsArray"
      :key="'selector-row-' + rowIndex"
      :ref="'selectorRow-' + rowIndex"
      class="selector-row"
      :class="{ active: rowIndex === activeRowIndex }"
      @click="setActiveRow(rowIndex)"
    >
      <div class="selector-bar" :class="{ 'view-only': timeLineEdit }">
        <template v-if="timeLineEdit">
          <template v-if="rowTags.length">
            <template v-for="(tag, index) in rowTags">
              <span
                :key="`${tag.id}-${index}-view-name`"
                class="view-crumb"
              >
                <span>{{ useAbbreviation && tag.abbreviation ? tag.abbreviation : tag.name }}</span>
                <span v-if="!useAbbreviation && tag.abbreviation" class="abbreviation-tag ml-1">{{ tag.abbreviation }}</span>
              </span>
              <span
                v-if="index !== rowTags.length - 1"
                :key="`${tag.id}-${index}-view-separator`"
                class="crumb-separator"
              >
                >
              </span>
            </template>
          </template>
          <span v-else class="view-crumb view-crumb--empty">--</span>
        </template>
        <template v-else>
          <button
            type="button"
            class="crumb root"
            :disabled="timeLineEdit"
            @click="onRootClick(rowIndex)"
          >
            Root
          </button>
          <span class="crumb-separator">></span>
          <template v-for="(tag, index) in rowTags">
            <button
              :key="tag.id"
              type="button"
              class="crumb tag"
              :class="{ active: index === rowTags.length - 1 }"
              :disabled="timeLineEdit"
              @click="focusCurrentLevel(rowIndex, index)"
            >
              <span>{{ useAbbreviation && tag.abbreviation ? tag.abbreviation : tag.name }}</span>
              <!-- <span v-if="!useAbbreviation && tag.abbreviation" class="abbreviation-tag ml-1">{{ tag.abbreviation }}</span> -->
            </button>
            <span
              v-if="index !== rowTags.length - 1"
              :key="`${tag.id}-separator`"
              class="crumb-separator"
            >
              >
            </span>
          </template>
          <button
            v-if="showAddButtonByRow[rowIndex] !== false"
            type="button"
            class="add-btn"
            :disabled="timeLineEdit"
            @click="toggleSuggestions(rowIndex)"
          >
            +
          </button>
        </template>
      </div>
      <div v-if="validationMessagesByRow[rowIndex]" class="selection-validation-message">
        {{ validationMessagesByRow[rowIndex] }}
      </div>

      <div
        v-if="suggestion && openSuggestionRowIndex === rowIndex"
        :ref="'resultBox-' + rowIndex"
        class="resultBox"
        :style="suggestionBoxStyle"
      >
        <div class="searchInput input-group">
          <input
            :ref="'filterInput-' + rowIndex"
            type="text"
            :disabled="timeLineEdit"
            placeholder="Filter..."
            :value="getFilterValue(rowIndex)"
            @input="onFilterInput($event, rowIndex)"
            @focus="onInputFocus(rowIndex)"
          >
        </div>
        <ul v-if="suggestions.length > 0">
          <li v-for="element in suggestions" :key="element.id" class="pointer" @click.stop="addTag(element, rowIndex)">
            <div style="display: flex; align-items: center; gap: 8px;">
              <span class="item-name">{{ element.name }}</span>
              <span v-if="element.abbreviation" class="abbreviation-tag">{{ element.abbreviation }}</span>
            </div>
            <span class="item-arrow">></span>
          </li>
        </ul>
        <span v-else-if="loading">Loading ...</span>
        <div v-else class="empty-state">
          <span>No {{ entityLabel }} Found</span>
          <!-- <span
            v-if="!disableSelectingAnotherParentTag"
            class="tag-modal-link"
            @click="showRootTags(rowIndex)"
          >
            Select {{ parentSelectorLabel }}
          </span> -->
        </div>
      </div>
    </div>
    <div v-if="allowMultipleRoots && !timeLineEdit" class="outer-add-wrap">
      <button
        type="button"
        class="outer-add-btn"
        :disabled="timeLineEdit"
        @click="addNewRootRow"
      >
        +
      </button>
    </div>
  </div>
</template>

<script>
import { SearchParentTags, SearchChildrenTags, GetCustomList, GetCustomListChild } from '@/api'
import { debounce } from '@/utils/debounce'
// import { alert } from '@/plugins/notification'
export default {
  name: 'tagAndConfigInput',
  props: {
    tags: {
      type: Array,
      default () { return [] }
    },
    title: {
      type: String,
      default () { return 'Search Tags' }
    },
    type: {
      type: Number
    },
    customListId: {
      type: [Number, String],
      default: null
    },
    lastParentId: {
      type: [String, Number],
      default: null
    },
    tagGroupData: {
      type: Object,
      default () { return {} }
    },
    timeLineEdit: { type: Boolean, default: false },
    disableSelectingAnotherParentTag: { type: Boolean, default: true },
    allowMultipleRoots: { type: Boolean, default: false },
    useAbbreviation: {
      type: Boolean,
      default: false
    },
    validateLeafSelection: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      value: '',
      filterValuesByRow: {},
      loading: false,
      showSuggestion: null,
      suggestion: false,
      suggestions: [],
      openTagModal: false,
      showError: false,
      selectedTags: [],
      pendingChildParentIdByRow: {},
      suggestionParentOverrideByRow: {},
      showAddButtonByRow: {},
      activeRowIndex: 0,
      openSuggestionRowIndex: null,
      virtualRowCount: 1,
      suggestionBoxStyle: {},
      validationMessagesByRow: {},
      validationRequestId: 0
    }
  },
  computed: {
    EmptyInputError () {
      return this.getFilterValue(this.activeRowIndex).trim() === ''
    },
    hasTags () {
      return this.tags?.length > 0
    },
    isCustomListMode () {
      return this.customListId !== null && this.customListId !== undefined && this.customListId !== ''
    },
    entityLabel () {
      return this.isCustomListMode ? 'Value' : 'Tag'
    },
    parentSelectorLabel () {
      return this.isCustomListMode ? 'Another Parent Value' : 'Another Parent Tag'
    },
    rowTagsArray () {
      const rows = this.tagGroupData?.tagArray || []
      if (!this.allowMultipleRoots) {
        return [rows[0] || this.tags || []]
      }
      const baseCount = rows.length || (this.tags?.length ? 1 : 0) || 1
      const totalCount = Math.max(baseCount, this.virtualRowCount)
      return Array.from({ length: totalCount }, (_, index) => rows[index] || (index === 0 ? (this.tags || []) : []))
    }
  },
  methods: {
    getRowTags (rowIndex = this.activeRowIndex) {
      return this.rowTagsArray[rowIndex] || []
    },
    getFilterValue (rowIndex = this.activeRowIndex) {
      return this.filterValuesByRow[rowIndex] || ''
    },
    setFilterValue (rowIndex, value = '') {
      this.$set(this.filterValuesByRow, rowIndex, value)
    },
    getSelectorRowEl (rowIndex) {
      const ref = this.$refs[`selectorRow-${rowIndex}`]
      const el = Array.isArray(ref) ? ref[0] : ref
      return el instanceof HTMLElement ? el : null
    },
    getResultBoxEl (rowIndex = this.openSuggestionRowIndex) {
      const ref = this.$refs[`resultBox-${rowIndex}`]
      const el = Array.isArray(ref) ? ref[0] : ref
      return el instanceof HTMLElement ? el : null
    },
    updateSuggestionPosition (rowIndex = this.openSuggestionRowIndex) {
      const rowEl = this.getSelectorRowEl(rowIndex)
      if (!rowEl) return
      const rect = rowEl.getBoundingClientRect()
      const width = Math.max(rect.width, 280)
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8))

      const boxEl = this.getResultBoxEl(rowIndex)
      let boxHeight = 280
      if (!this.loading && boxEl) {
        boxHeight = boxEl.offsetHeight > 0 ? boxEl.offsetHeight : Math.min(boxEl.scrollHeight || 280, 280)
      }

      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top
      const noSpaceDown = spaceBelow < boxHeight + 8

      const style = {
        position: 'fixed',
        left: `${left}px`,
        width: `${width}px`,
        zIndex: 10000
      }

      if (noSpaceDown && spaceAbove > spaceBelow) {
        style.bottom = `${window.innerHeight - rect.top + 4}px`
        style.top = 'auto'
        style.maxHeight = `${Math.min(280, spaceAbove - 16)}px`
      } else {
        style.top = `${rect.bottom + 4}px`
        style.bottom = 'auto'
        style.maxHeight = `${Math.min(280, spaceBelow - 16)}px`
      }

      this.suggestionBoxStyle = style
    },
    openSuggestionForRow (rowIndex) {
      this.suggestion = true
      this.openSuggestionRowIndex = rowIndex
      this.$nextTick(() => {
        this.updateSuggestionPosition(rowIndex)
      })
    },
    handleViewportChange () {
      if (this.suggestion && this.openSuggestionRowIndex !== null) {
        this.$nextTick(() => this.updateSuggestionPosition(this.openSuggestionRowIndex))
      }
    },
    onFilterInput (event, rowIndex = this.activeRowIndex) {
      const targetValue = event?.target?.value || ''
      this.setFilterValue(rowIndex, targetValue.trim())
      this.showSuggestion(rowIndex)
    },
    emitRowSelected (rowIndex) {
      this.$emit('parentLevelSelected', { id: null, rowIndex })
    },
    getTagParentIdByIndex (rowIndex, index) {
      const rowTags = this.getRowTags(rowIndex)
      if (!rowTags.length || index < 0 || index >= rowTags.length) return null
      const currentTag = rowTags[index]
      const directParentId = currentTag?.parentId ?? currentTag?.parent_id
      if (directParentId !== undefined && directParentId !== null && directParentId !== '') {
        return directParentId
      }
      if (index > 0) {
        return rowTags[index - 1]?.id ?? null
      }
      return null
    },
    getSuggestionParentId (rowIndex = this.activeRowIndex) {
      const overrideParentId = this.suggestionParentOverrideByRow[rowIndex]
      if (overrideParentId !== null && overrideParentId !== undefined && overrideParentId !== '') {
        return overrideParentId
      }
      const rowTags = this.getRowTags(rowIndex)
      return rowTags[rowTags.length - 1]?.id ?? null
    },
    setActiveRow (rowIndex) {
      const nextRowIndex = this.allowMultipleRoots ? rowIndex : 0
      this.activeRowIndex = nextRowIndex
      this.emitRowSelected(nextRowIndex)
    },
    async addTag (tag, rowIndex = this.activeRowIndex) {
      if (tag) {
        this.setActiveRow(rowIndex)
        const hasChildren = await this.hasChildOptions(tag)
        const normalizedTag = {
          ...tag,
          parentId: tag.parent_id ?? tag.parentId ?? null,
          hasChildren
        }
        this.$emit('addNewTag', { tag: normalizedTag, rowIndex })
        if (normalizedTag.parentId === null) {
          this.$emit('parentLevelSelected', { id: normalizedTag.id, rowIndex })
        }
        this.$set(this.suggestionParentOverrideByRow, rowIndex, null)
        this.setFilterValue(rowIndex, '')
        this.$set(this.showAddButtonByRow, rowIndex, hasChildren)
        if (hasChildren) {
          this.$set(this.validationMessagesByRow, rowIndex, this.getLeafSelectionMessage())
          this.emitValidationState(false)
          this.$set(this.pendingChildParentIdByRow, rowIndex, normalizedTag.id)
          this.$set(this.suggestionParentOverrideByRow, rowIndex, normalizedTag.id)
          this.openSuggestionForRow(rowIndex)
          this.suggestions = []
          this.getData(rowIndex)
          return
        }
        this.$delete(this.validationMessagesByRow, rowIndex)
        this.validateSelection()
        this.$set(this.pendingChildParentIdByRow, rowIndex, null)
        this.suggestions = []
        this.suggestion = false
        this.openSuggestionRowIndex = null
        this.suggestionBoxStyle = {}
      }
    },
    hideSuggestion () {
      this.suggestion = false
      this.openSuggestionRowIndex = null
      this.suggestionBoxStyle = {}
    },
    hasPendingChildSelection () {
      return Object.values(this.pendingChildParentIdByRow || {}).some(
        value => value !== null && value !== undefined && value !== ''
      )
    },
    handleDocumentClick (event) {
      if (!this.$el?.contains(event.target) && !this.hasPendingChildSelection()) {
        this.hideSuggestion()
      }
    },
    onInputFocus (rowIndex = this.activeRowIndex) {
      this.setActiveRow(rowIndex)
      this.openSuggestionForRow(rowIndex)
    },
    async refreshAddButtonVisibility () {
      const rows = this.rowTagsArray
      for (let i = 0; i < rows.length; i++) {
        const rowTags = rows[i]
        if (!rowTags?.length) {
          this.$set(this.showAddButtonByRow, i, true)
          continue
        }
        const lastSelectedTag = rowTags[rowTags.length - 1]
        const hasChildren = await this.hasChildOptions(lastSelectedTag)
        this.$set(this.showAddButtonByRow, i, hasChildren)
      }
    },
    focusCurrentLevel (rowIndex, index) {
      if (this.timeLineEdit) return
      this.setActiveRow(rowIndex)
      this.$set(this.suggestionParentOverrideByRow, rowIndex, this.getTagParentIdByIndex(rowIndex, index))
      this.$emit('focusTagLevel', { index, rowIndex })
      this.$delete(this.validationMessagesByRow, rowIndex)
      this.openSuggestionForRow(rowIndex)
      this.$nextTick(() => this.getData(rowIndex))
    },
    toggleSuggestions (rowIndex = this.activeRowIndex) {
      if (this.timeLineEdit) return
      this.setActiveRow(rowIndex)
      this.suggestion = !this.suggestion
      this.openSuggestionRowIndex = this.suggestion ? rowIndex : null
      if (this.suggestion) {
        this.$set(this.suggestionParentOverrideByRow, rowIndex, null)
        this.getData(rowIndex)
        this.$nextTick(() => {
          this.updateSuggestionPosition(rowIndex)
          const inputEl = this.getFilterInput(rowIndex)
          if (inputEl) inputEl.focus()
        })
      } else {
        this.suggestionBoxStyle = {}
      }
    },
    getFilterInput (rowIndex) {
      const ref = this.$refs[`filterInput-${rowIndex}`]
      const el = Array.isArray(ref) ? ref[0] : ref
      // el could be a Vue instance, a DOM element, or undefined
      if (!el) return null
      if (el instanceof HTMLElement) return el
      if (el.$el instanceof HTMLElement) return el.$el
      return null
    },
    normalizeId (id) {
      const converted = Number(id)
      return Number.isNaN(converted) ? id : converted
    },
    getChildCountFromItem (item) {
      if (!item) return null
      const aggregateCount = item?.child_values_aggregate?.aggregate?.count
      return typeof aggregateCount === 'number' ? aggregateCount : null
    },
    async hasChildOptions (item) {
      if (item && item.hasChildren !== undefined) {
        return item.hasChildren
      }
      const aggregateCount = this.getChildCountFromItem(item)
      if (aggregateCount !== null) {
        return aggregateCount > 0
      }
      const id = item?.id ?? item

      if (!this._childCountPromises) {
        this._childCountPromises = {}
      }
      const cacheKey = `${this.isCustomListMode ? 'custom' : 'tag'}-${id}`
      if (cacheKey in this._childCountPromises) {
        return this._childCountPromises[cacheKey]
      }

      const fetchPromise = (async () => {
        if (this.isCustomListMode) {
          const items = await this.getCustomListChildOptions(id)
          return items.length > 0
        }
        if (this.$props.type) {
          const items = await this.getSearchChildrenTags(id, this.$props.type)
          return items.length > 0
        }
        return false
      })()

      this._childCountPromises[cacheKey] = fetchPromise
      try {
        const result = await fetchPromise
        this._childCountPromises[cacheKey] = result
        return result
      } catch (err) {
        delete this._childCountPromises[cacheKey]
        throw err
      }
    },
    getLeafSelectionMessage () {
      return `Please select the last child ${this.entityLabel.toLowerCase()} before continuing.`
    },
    emitValidationState (isValid, messages = this.validationMessagesByRow) {
      this.$emit('validation-change', {
        isValid,
        messages
      })
    },
    async validateRowSelection (rowIndex) {
      if (!this.validateLeafSelection || this.timeLineEdit) {
        this.$delete(this.validationMessagesByRow, rowIndex)
        return true
      }
      const rowTags = this.getRowTags(rowIndex)
      if (!rowTags.length) {
        this.$delete(this.validationMessagesByRow, rowIndex)
        return true
      }
      const lastSelectedTag = rowTags[rowTags.length - 1]
      const hasChildren = await this.hasChildOptions(lastSelectedTag)
      if (hasChildren) {
        this.$set(this.validationMessagesByRow, rowIndex, this.getLeafSelectionMessage())
        return false
      }
      this.$delete(this.validationMessagesByRow, rowIndex)
      return true
    },
    async validateSelection () {
      const requestId = ++this.validationRequestId
      const results = await Promise.all(
        this.rowTagsArray.map((row, rowIndex) => this.validateRowSelection(rowIndex))
      )
      if (requestId !== this.validationRequestId) return false
      const isValid = results.every(Boolean)
      this.emitValidationState(isValid)
      return isValid
    },
    async getRootOptionsByCustomListId () {
      if (!this.isCustomListMode) return []
      const listId = this.normalizeId(this.customListId)
      if (!this._rootOptionsPromises) this._rootOptionsPromises = {}
      if (this._rootOptionsPromises[listId]) {
        return this._rootOptionsPromises[listId]
      }

      const fetchPromise = GetCustomList(listId)
        .then(res => res?.core_custom_list_by_pk?.custom_list_values || [])
      this._rootOptionsPromises[listId] = fetchPromise
      try {
        return await fetchPromise
      } catch (err) {
        delete this._rootOptionsPromises[listId]
        throw err
      }
    },
    async getCustomListChildOptions (parentId) {
      const id = this.normalizeId(parentId)
      if (!this._childOptionsPromises) this._childOptionsPromises = {}
      if (this._childOptionsPromises[id]) {
        return this._childOptionsPromises[id]
      }

      const fetchPromise = GetCustomListChild(id)
        .then(res => res?.custom_list_values || [])
      this._childOptionsPromises[id] = fetchPromise
      try {
        return await fetchPromise
      } catch (err) {
        delete this._childOptionsPromises[id]
        throw err
      }
    },
    async getSearchChildrenTags (parentId, type) {
      if (!this._childrenTagsPromises) this._childrenTagsPromises = {}
      const cacheKey = `${parentId}-${type}`
      if (this._childrenTagsPromises[cacheKey]) {
        return this._childrenTagsPromises[cacheKey]
      }

      const fetchPromise = SearchChildrenTags(parentId, '', type)
        .then(res => res?.tag || [])

      this._childrenTagsPromises[cacheKey] = fetchPromise
      try {
        return await fetchPromise
      } catch (err) {
        delete this._childrenTagsPromises[cacheKey]
        throw err
      }
    },
    async getData (rowIndex = this.activeRowIndex) {
      const resolvedRowIndex = typeof rowIndex === 'number' ? rowIndex : this.activeRowIndex
      const queryValue = this.getFilterValue(resolvedRowIndex)
      this.loading = true
      const suggestionParentId = this.getSuggestionParentId(resolvedRowIndex)
      if (this.isCustomListMode) {
        if (!suggestionParentId) {
          this.getRootOptionsByCustomListId()
            .then((res) => {
              const items = res || []
              this.suggestions = queryValue ? items.filter(item => item.name?.toLowerCase().includes(queryValue.toLowerCase()) || item.abbreviation?.toLowerCase().includes(queryValue.toLowerCase())) : items
            })
            .catch((err) =>
              console.log(err)
            ).finally(() => {
              this.loading = false
            })
          return
        }
        this.getCustomListChildOptions(suggestionParentId)
          .then((res) => {
            const items = res || []
            this.suggestions = queryValue ? items.filter(item => item.name?.toLowerCase().includes(queryValue.toLowerCase()) || item.abbreviation?.toLowerCase().includes(queryValue.toLowerCase())) : items
          })
          .catch((err) =>
            console.log(err)
          ).finally(() => {
            this.loading = false
          })
        return
      }
      if (!suggestionParentId) {
        SearchParentTags(queryValue, this.$props.type)
          .then((res) => {
            this.suggestions = res.tag
          }
          )
          .catch((err) =>
            console.log(err)
          ).finally(() => {
            this.loading = false
          })
        return
      }

      if (!queryValue) {
        this.getSearchChildrenTags(suggestionParentId, this.$props.type)
          .then((tags) => {
            this.suggestions = tags
          })
          .catch((err) => console.log(err))
          .finally(() => {
            this.loading = false
          })
        return
      }

      SearchChildrenTags(suggestionParentId, queryValue, this.$props.type)
        .then((res) => {
          this.suggestions = res.tag
        }
        )
        .catch((err) =>
          console.log(err)
        ).finally(() => {
          this.loading = false
        })
    },
    validateInput () {
      this.showError = this.getFilterValue(this.activeRowIndex).trim() === ''
    },
    showRootTags (rowIndex = this.activeRowIndex) {
      this.setActiveRow(rowIndex)
      this.openSuggestionForRow(rowIndex)
      this.loading = true
      this.setFilterValue(rowIndex, '')
      this.$set(this.suggestionParentOverrideByRow, rowIndex, null)
      const rootRequest = this.isCustomListMode
        ? this.getRootOptionsByCustomListId()
        : SearchParentTags(this.getFilterValue(rowIndex), this.$props.type).then((res) => res?.tag || [])
      rootRequest
        .then((rootItems) => {
          const items = Array.isArray(rootItems) ? rootItems : []
          if (this.allowMultipleRoots && Array.isArray(this.tagGroupData?.tagArray)) {
            const otherRowRootIds = this.tagGroupData.tagArray
              .filter((_, idx) => idx !== rowIndex)
              .map(row => row[0]?.id)
              .filter(Boolean)
            this.suggestions = otherRowRootIds.length
              ? items.filter(item => !otherRowRootIds.includes(item.id))
              : items
          } else {
            this.suggestions = items
          }
        })
        .catch((err) =>
          console.log(err)
        ).finally(() => {
          this.loading = false
          this.$nextTick(() => {
            this.updateSuggestionPosition(rowIndex)
            const filterInput = this.getFilterInput(rowIndex)
            if (filterInput) filterInput.focus()
          })
        })
      this.$emit('parentLevelSelected', { id: null, rowIndex })
    },
    onRootClick (rowIndex = this.activeRowIndex) {
      if (this.timeLineEdit) return
      this.setActiveRow(rowIndex)
      this.$emit('resetSelectedLine', { rowIndex })
      this.$set(this.pendingChildParentIdByRow, rowIndex, null)
      this.$set(this.suggestionParentOverrideByRow, rowIndex, null)
      this.$set(this.showAddButtonByRow, rowIndex, true)
      this.$delete(this.validationMessagesByRow, rowIndex)
      this.showRootTags(rowIndex)
    },
    addNewRootRow () {
      if (this.timeLineEdit || !this.allowMultipleRoots) return
      const nextRowIndex = this.rowTagsArray.length
      this.virtualRowCount = nextRowIndex + 1
      this.setActiveRow(nextRowIndex)
      this.$set(this.showAddButtonByRow, nextRowIndex, true)
      this.showRootTags(nextRowIndex)
    }
  },
  watch: {
    suggestion (isOpen) {
      this.$emit('dropdown-state-change', isOpen)
    },
    suggestions () {
      if (this.suggestion && this.openSuggestionRowIndex !== null) {
        this.$nextTick(() => this.updateSuggestionPosition(this.openSuggestionRowIndex))
      }
    },
    loading () {
      if (this.suggestion && this.openSuggestionRowIndex !== null) {
        this.$nextTick(() => this.updateSuggestionPosition(this.openSuggestionRowIndex))
      }
    },
    tags: {
      immediate: true,
      deep: true,
      handler () {
        this.refreshAddButtonVisibility()
      }
    },
    tagGroupData: {
      immediate: true,
      deep: true,
      handler () {
        if (this.allowMultipleRoots) {
          const realRows = this.tagGroupData?.tagArray?.length || 0
          if (realRows > this.virtualRowCount) {
            this.virtualRowCount = realRows
          }
        } else {
          this.virtualRowCount = 1
          this.activeRowIndex = 0
        }
        if (this.activeRowIndex >= this.rowTagsArray.length) {
          this.activeRowIndex = Math.max(0, this.rowTagsArray.length - 1)
        }
        this.refreshAddButtonVisibility()
        this.validateSelection()
      }
    },
    allowMultipleRoots: {
      immediate: true,
      handler (value) {
        if (!value) {
          this.virtualRowCount = 1
          this.activeRowIndex = 0
          if (this.openSuggestionRowIndex !== null && this.openSuggestionRowIndex !== 0) {
            this.openSuggestionRowIndex = 0
          }
        }
      }
    },
    lastParentId (newValue) {
      const pendingParentId = this.pendingChildParentIdByRow[this.activeRowIndex]
      if (pendingParentId && newValue === pendingParentId) {
        this.suggestion = true
        this.suggestions = []
        this.$set(this.pendingChildParentIdByRow, this.activeRowIndex, null)
        this.getData(this.activeRowIndex)
      }
    }
  },
  beforeDestroy () {
    window.document.removeEventListener('click', this.handleDocumentClick)
    window.removeEventListener('resize', this.handleViewportChange)
    window.removeEventListener('scroll', this.handleViewportChange, true)
    this.$emit('update-tags', [])
  },
  created () {
    this.showSuggestion = debounce(this.getData, 300, this)
  },
  mounted () {
    window.document.addEventListener('click', this.handleDocumentClick)
    window.addEventListener('resize', this.handleViewportChange)
    window.addEventListener('scroll', this.handleViewportChange, true)
  }
}
</script>

<style scoped lang="scss">
.selector-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 5px;
}

.selector-row {
  position: relative;

  &.active .selector-bar {
    border-color: #fff7ed;
  }
}

.outer-add-wrap {
  display: flex;
  justify-content: flex-start;
}

.outer-add-btn {
  width: 30px;
  height: 30px;
  border: 1px dashed #9eb2cc;
  border-radius: 8px;
  background: #edf3fb;
  color: #3d5f87;
  font-size: 21px;
  font-weight: 500;
  line-height: 1;
  padding: 0;
}

.selector-bar {
  min-height: 42px;
  max-width: 100%;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 1px solid #d7dee8;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(35, 38, 47, 0.06);
  padding: 6px 8px;

  &.disabled {
    opacity: 0.7;
    pointer-events: none;
  }

  &.view-only {
    overflow-x: auto;
    pointer-events: none;
  }
}

.selection-validation-message {
  margin: 6px 2px 0;
  color: #c53030;
  font-size: var(--text-xs2);
}

.view-crumb {
  color: #64748b;
  font-size: var(--text-m);
  font-weight: 500;
  line-height: 1;
  padding: 7px 10px;
  white-space: nowrap;
  overflow-wrap: anywhere;
  display: inline-flex;
  align-items: center;
}

.view-crumb--empty {
  color: #94a3b8;
}

.crumb {
  border: 0;
  background: transparent;
  color: #334155;
  font-size: var(--text-m);
  font-weight: 500;
  line-height: 1;
  border-radius: 7px;
  padding: 7px 10px;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;

  &.root {
    color: var(--brand-color-1);
  }

  &.active {
    background: #fff7ed;
    color: var(--brand-color-1);
  }
}

.crumb-separator {
  color: #a7b1c2;
  font-size: 15px;
  font-weight: 500;
  line-height: 1;
}

.add-btn {
  width: 30px;
  height: 30px;
  border: 1px dashed #b8c2d3;
  border-radius: 8px;
  background: #f7f9fc;
  color: #7f8ca2;
  font-size: 21px;
  font-weight: 500;
  line-height: 1;
  padding: 0;
}

.resultBox {
  position: fixed;
  top: -9999px;
  left: -9999px;
  width: 280px;
  max-width: 90vw;
  max-height: 280px;
  overflow: auto;
  background: #fff;
  border: 1px solid #d7dee8;
  border-radius: 10px;
  box-shadow: 0 8px 18px rgba(18, 26, 42, 0.12);
  z-index: 10000;
  padding: 10px;

  ul {
    padding: 4px 0 0;
    margin: 0;
  }

  li {
    list-style: none;
    padding: 10px 8px;
    border-radius: 7px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: #334155;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
  }

  li:hover {
    background: #fff7ed;
  }
}

.searchInput {
  input {
    width: 100%;
    border: 1px solid #cfd8e6;
    border-radius: 8px;
    height: 34px;
    padding: 0 10px;
    font-size: 14px;
    color: #334155;
    outline: none;
  }

  input::placeholder {
    color: #94a3b8;
  }

  input:focus {
    border-color: #fff7ed;
    box-shadow: 0 0 0 2px rgba(31, 105, 183, 0.14);
  }
}

.item-arrow {
  color: #7f8ca2;
  font-size: 14px;
  font-weight: 500;
}

.item-name {
  font-size: var(--text-m);
}

.empty-state {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 4px;
  font-size: 14px;
  color: #55657d;
}

.tag-modal-link {
  color: var(--brand-color);
  text-decoration: underline;
  font-weight: 500;
  cursor: pointer;
}

.abbreviation-tag {
  font-size: var(--text-xs);
  background: #f4f4f4;
  padding: 1px 5px;
  border-radius: 3px;
  color: #666;
  border: 1px solid #e0e0e0;
  font-weight: 600;
  text-transform: uppercase;
}
</style>
